import React, { useState, useMemo, useRef, useEffect } from 'react'
import { FontAwesomeIcon }                from '@fortawesome/react-fontawesome'
import FullCalendar                       from '@fullcalendar/react' // must go before plugins
import { useCalendar }                    from './useCalendar'

// plugins
import interactionPlugin          from "@fullcalendar/interaction"
import resourceTimelinePlugin     from '@fullcalendar/resource-timeline'
import resourceTimeGridPlugin     from '@fullcalendar/resource-timegrid'
import scrollGrid                 from '@fullcalendar/scrollgrid'
import resourceDayGridPlugin      from '@fullcalendar/resource-daygrid'
import resource                   from '@fullcalendar/resource'
import timeGridPlugin             from '@fullcalendar/timegrid'
import daygridplugin              from '@fullcalendar/daygrid'

// helpers
import { CALENDAR_VIEWS, getCurrentView } from '@helpers/javascript/calendar_helper'
import { postRequest, deleteRequest, pathname } from '@helpers/javascript/javascript'
import { modalRequest }                   from '../helpers/fetch'

const RESOURCE_ROW_PREFIX = "resource_"
const RESOURCES_GROUP_ID  = "resources_group"
const isResourceRow = id => typeof id === "string" && id.startsWith(RESOURCE_ROW_PREFIX)
const isMaterialPlanningRow = id => isResourceRow(id) || id === RESOURCES_GROUP_ID

// Components
import Combobox       from "@shared/combobox"
import OpenEventsList from './openEventsList'
import EventCard      from './eventCard'
import { confirmScheduleConflict } from './conflictConfirm'
import UserLabel      from "./userLabel"
import StackedCalendar from './stackedCalendar'

const CalendarRight = () => {
  const { state, setState, TOOLS, filter, setFilter, fetchEvents, fetchAllEvents, openAbsenceModal } = useCalendar()
  const SEPARATED = "separated"
  const [fetching,       setFetching]       = useState()
  const [dropping,       setDropping]       = useState(false)

  // Alt (⌥ on a Mac) held when the drag STARTED. FullCalendar reports the
  // mouseup in `select`, so releasing the key before the mouse would lose the
  // modifier — we record it on mousedown instead. Cmd (⌘) is accepted too.
  const absenceDragRef = useRef(false)
  useEffect(() => {
    const onDown = e => { absenceDragRef.current = !!(e.altKey || e.metaKey) }
    document.addEventListener('mousedown', onDown, true)
    return () => document.removeEventListener('mousedown', onDown, true)
  }, [])

  // ── Day load per technician (single-day views only) ─────────────────────
  // Computed from the events already on screen, so the gauge reacts live to
  // every drag & drop. Minutes of workorders clipped to the displayed day.
  const singleDayView = [CALENDAR_VIEWS.day, CALENDAR_VIEWS.agenda_day].includes(filter.view.name)

  const dayLoads = useMemo(() => {
    if (!singleDayView)                       return null
    if (!state.events || !state.currentDate)  return null

    const dayStart = new Date(state.currentDate)
    dayStart.setHours(0, 0, 0, 0)
    const dayEnd = new Date(dayStart)
    dayEnd.setDate(dayEnd.getDate() + 1)

    const loads = {}
    state.events.forEach(ev => {
      if (!ev.resourceId || ev.resourceId === 'open')          return
      if (!(ev.className || '').includes('workorder-event'))   return
      const from = Math.max(new Date(ev.start), dayStart)
      const to   = Math.min(new Date(ev.end),   dayEnd)
      if (to <= from) return
      loads[String(ev.resourceId)] = (loads[String(ev.resourceId)] || 0) + (to - from) / 60000
    })
    return loads
  }, [singleDayView, state.events, state.currentDate])

  const renderLabelContent = resource =>
    <UserLabel
      resource     = { resource }
      dayLoads     = { dayLoads }
      rawResources = { TOOLS.resources }
      currentDate  = { state.currentDate }
      onOpenAbsences = { TOOLS.newAbsences
        ? technicianId => openAbsenceModal({
            technicianId,
            anchorDate: (state.currentDate || state.startDate) ? new Date(state.currentDate || state.startDate).toISOString() : undefined,
          })
        : null }
      popupAction = { newState =>
        setState(prevState => ({
          ...prevState,
          showPopup: newState
        }))}
    />

  const handleSelect = e => {
    const technicianRow = e.resource?._resource?.parentId && !isMaterialPlanningRow(e.resource._resource.id)
    const withModifier  = absenceDragRef.current || e.jsEvent?.altKey || e.jsEvent?.metaKey
    if (TOOLS.newAbsences && withModifier) {
      absenceDragRef.current = false
      state.calendar?.unselect()
      openAbsenceModal({
        technicianId: technicianRow ? e.resource._resource.id : undefined,
        startDate:    e.start.toISOString(),
        endDate:      e.end.toISOString(),
        anchorDate:   e.start.toISOString(),
        allDay:       e.allDay, // a range traced over hours stays hourly
      })
      return
    }

    const path = TOOLS.workorderFromCalendar === 'modal' ? '/calendar/workorders/new' : '/workorders/new'
    const params = {
      date_planned:       e.startStr,
      predicted_duration: (e.end - e.start) / 60000
    }
    if (e.resource) {
      params.technician = e.resource?._resource?.parentId ? e.resource._resource.id : "",
      params.manager    = e.resource?._resource?.parentId ? e.resource._resource.parentId : e.resource._resource.id
    }

    const url = pathname({ path, params })

    if (TOOLS.workorderFromCalendar === 'modal') {
      return modalRequest(url)
    } else { window.location = url }
  }

  const fullDayView = () => [CALENDAR_VIEWS.assignment, CALENDAR_VIEWS.month].includes(filter.view.name)

  // ── Resource bookings (material resources) ────────────────────────────
  const upsertEvent = event => setState(prevState => ({
    ...prevState,
    events: [...prevState.events.filter(e => e.id !== event.id), event]
  }))

  const createBooking = (workorderId, resourceRowId, confirmConflict = false) => {
    const resourceId = String(resourceRowId).replace(RESOURCE_ROW_PREFIX, "")
    const url        = pathname({ path: '/resource_bookings', format: 'json' })
    const body       = { workorder_id: workorderId, resource_id: resourceId, confirm_conflict: confirmConflict }

    postRequest({ url, body }).then(res => {
      if (res && res.conflict) {
        const list = (res.conflict.bookings || []).map(b => [b.code, b.title].filter(Boolean).join(' ')).join(', ')
        if (window.confirm(I18n.t('resources.planning.conflict_confirm_js', { resource: res.conflict.resource_name, list: list }))) {
          createBooking(workorderId, resourceRowId, true)
        }
      } else if (res && res.event) {
        upsertEvent(res.event)
      }
    })
  }

  const removeBooking = (bookingId, workorderId) => {
    const url = pathname({ path: `/resource_bookings/${bookingId}`, format: 'json', params: { workorder_id: workorderId } })
    deleteRequest(url).then(res => {
      if (res && res.ok) {
        setState(prevState => ({ ...prevState, events: prevState.events.filter(e => e.id !== `booking_${bookingId}`) }))
      }
    })
  }

  const handleEventResize = info => {
    const path   = '/calendar/update_resize'
    const format = "json"
    const url    = pathname({ path, format })

    const submit = confirmed => {
      const body = {
        event: {
          workorder_id:       info.event._def.extendedProps.info.workorder_id,
          start:              info.event._instance.range.start,
          end:                info.event._instance.range.end,
          confirmed_conflict: confirmed
        }
      }
      postRequest({ url, body }).then(res => {
        if (res.conflict) {
          if (confirmScheduleConflict(res.conflict)) return submit(true)
          return info.revert()
        }
        if (res.unprocessed) info.revert()
        const newEvents = [...state.events]
        const changedEvent = newEvents.findIndex(ev => res.event.info.workorder_id === ev.info?.workorder_id)
        newEvents[changedEvent] = res.event
        setState(prevState=>({...prevState, events: newEvents}))
      })
    }

    submit(false)
  }

  const handleInternalDrop = info => {
    // The main calendar is resource-based, so a dropped event carries the target
    // row in `resourceIds`. The stacked calendar renders one non-resource timeGrid
    // per technician (no resource plugin), so its events have no `resourceIds`;
    // fall back to the resourceId the event was built with — the technician its
    // grid belongs to, which an internal move can't change there.
    const targetResource = info.event._def.resourceIds?.[0] ?? info.event._def.extendedProps.resourceId
    // Dropped onto a material-resource row → create a booking, keep the
    // intervention on its technician row (the move is reverted).
    if (isResourceRow(targetResource)) {
      const workorderId = info.event._def.extendedProps.info.workorder_id
      info.revert()
      createBooking(workorderId, targetResource)
      return
    }

    const path = '/calendar/update_drop'
    const format = "json"
    const url = pathname({ path, format })

    const submit = confirmed => {
      const body = {
        event: {
          workorder_id:       info.event._def.extendedProps.info.workorder_id,
          time_change:        info.delta,
          resourceId:         targetResource,
          confirmed_conflict: confirmed
        }
      }
      postRequest({ url, body })
      .then((res) => {
        if (res.conflict) {
          if (confirmScheduleConflict(res.conflict)) return submit(true)
          return info.revert()
        }
        if (res.unprocessed) {
          info.revert()
        } else {
          const newEvents = [...state.events]
          const changedEvent = newEvents.findIndex(ev => res.event.info.workorder_id === ev.info?.workorder_id)
          newEvents[changedEvent] = res.event
          setState(prevState => ({ ...prevState, events: newEvents }))
        }
      })
    }

    submit(false)
  }

  const handleDatesSet = e => {
    if(fetching) clearTimeout(fetching)

    setState(prevState=>({
      ...prevState,
      calendar:    e.view.calendar,
      currentDate: e.view.calendar.currentData.currentDate,
      startDate:   e.start,
      endDate:     e.end
    }))
    setFetching(setTimeout(() => { fetchEvents({start: e.start, end: e.end, callback: (e)=>setState(prevState=>({...prevState, events: e}))}) }, 500))
  }

  const swallow = e => { e.preventDefault(); e.stopPropagation() }

  // Removing a leave straight from the planning band. The band is small and
  // the trash easy to mis-hit, so the confirmation names the absence.
  const deleteAbsence = (absenceId, label) => {
    const question = [I18n.t('users.absences.delete_confirm'), label].filter(Boolean).join('\n\n')
    if (!window.confirm(question)) return

    deleteRequest(pathname({ path: `absences/${absenceId}` })).then(res => {
      if (res.unprocessed) return
      fetchAllEvents()
    })
  }

  // Removing a band removes every day it covers.
  const deleteAssignment = (ids, label) => {
    const question = [I18n.t('projects.assignments.delete_confirm'), label].filter(Boolean).join('\n\n')
    if (!window.confirm(question)) return

    postRequest({ url: pathname({ path: 'projects/planning/assignments/delete' }), body: { ids } }).then(res => {
      if (res?.unprocessed) return
      fetchAllEvents()
    })
  }

  const editAbsence = props => openAbsenceModal({
    absenceId:    props.absence_id,
    technicianId: props.user_id,
    startDate:    props.start_at,
    endDate:      props.end_at,
    anchorDate:   props.start_at,
    allDay:       props.all_day,
    reason:       props.reason,
    categoryId:   props.category_id,
    onCallDuty:   props.on_call_duty,
  })

  const renderEventContent = eventInfo => {
    const props      = eventInfo.event._def.extendedProps
    const absence    = props.absence_id
    const assignment = props.project_assignment_ids
    const info       = props.info
    if (props.holiday) {
      return <span className="holiday-band">{eventInfo.event._def.title}</span>
    }
    if (assignment) {
      return (
        <div className="assignment-band">
          <FontAwesomeIcon icon="helmet-safety" className="assignment-band__icon" />
          <span className="assignment-band__title" title={props.project_name}>
            {eventInfo.event._def.title}
          </span>
          { props.deletable &&
            <span
              className     = "assignment-band__delete"
              title         = {I18n.t('projects.assignments.remove')}
              onPointerDown = {swallow}
              onMouseDown   = {swallow}
              onClick       = {e => { swallow(e); deleteAssignment(assignment, eventInfo.event._def.title) }}
            >
              <FontAwesomeIcon icon="trash" />
            </span>
          }
        </div>
      )
    }
    if (absence) {
      return (
        <div className="absence-band">
          <span
            className     = {`absence-band__title${props.updatable ? ' is-editable' : ''}`}
            title         = {props.updatable ? I18n.t('users.absences.edit') : null}
            onPointerDown = {props.updatable ? swallow : null}
            onMouseDown   = {props.updatable ? swallow : null}
            onClick       = {props.updatable ? (e => { swallow(e); editAbsence(props) }) : null}
          >
            {eventInfo.event._def.title}
          </span>
          { props.deletable &&
            <span
              className = "absence-band__delete"
              title     = {I18n.t('users.absences.delete_absence')}
              onPointerDown = {swallow}
              onMouseDown   = {swallow}
              onClick       = {e => { swallow(e); deleteAbsence(absence, eventInfo.event._def.title) }}
            >
              <FontAwesomeIcon icon="trash" />
            </span>
          }
        </div>
      )
    } else if (info) {
      return (
        <EventCard
          info             ={info}
          showDate         ={!fullDayView()}
          showDuration     ={fullDayView()}
          onRemoveBooking  ={removeBooking}
        />
      )
    }
  }

  const handleExternalDrop = info => {
    if (dropping) return

    const event  = JSON.parse(info.draggedEl.dataset.info)

    // Dropped from the open-workorders list onto a material-resource row → book it.
    if (info.resource && isResourceRow(info.resource._resource.id)) {
      createBooking(event.workorder_id, info.resource._resource.id)
      return
    }

    setDropping(true)
    const path   = '/calendar/update_drop'
    const format = "json"
    const date   = info.date

    // Named timeZone without a tz plugin: the UTC fields hold the account's wall-clock time.
    if (info.allDay) {
      const [hours, minutes] = event.event_start.split(":")
      date.setUTCHours(hours, minutes)
    }

    const url = pathname({ path, format })

    const submit = confirmed => {
      const body = {
        event: {
          workorder_id:       event.workorder_id,
          resourceId:         info.resource ? info.resource._resource.id : "open",
          new_date_planned:   date.toISOString(),
          confirmed_conflict: confirmed
        }
      }
      postRequest({ url, body }).then(res => {
        if (res.conflict) {
          if (confirmScheduleConflict(res.conflict)) return submit(true)
          setDropping(false)
          return
        }
        if (res.unprocessed) {
          setDropping(false)
          return
        }
        const newOpenEvents = state.openEvents.filter(wo => wo.info.workorder_id !== event.workorder_id)
        const changedEvent  = state.events.findIndex(ev => res.event.info.workorder_id === ev.info?.workorder_id)
        const newEvents = [...state.events]
        if (changedEvent !== -1) {
          newEvents[changedEvent] = res.event
        } else {
          newEvents.push(res.event)
        }
        setState(prevState=>({
          ...prevState,
          openEvents:      newOpenEvents,
          openEventsTotal: Math.max((prevState.openEventsTotal || 0) - (prevState.openEvents.length - newOpenEvents.length), 0),
          events:          newEvents
        }))
        setDropping(false)
      })
    }

    submit(false)
  }

  const customButtons = {}
  const calendarViews = {}

  for (const view in CALENDAR_VIEWS) {
    calendarViews[view] = {id: view, name: I18n.t(`preferences.calendar.view_${view}`)}
  }

  if (TOOLS.newAbsences) {
    // Open the form on the day the dispatcher is looking at, not on "today".
    const shownDate = () => {
      const date = state.currentDate || state.startDate
      return date ? new Date(date).toISOString() : undefined
    }
    customButtons.absence = {
      text:  I18n.t('calendar.new_absence_button'),
      hint:  I18n.t('users.absences.drag_hint'),
      click: () => openAbsenceModal({ anchorDate: shownDate() })
    },
    customButtons.onCallDuty = {
      text: I18n.t('users.absences.on_call_duty'),
      click: () => openAbsenceModal({ anchorDate: shownDate(), onCallDuty: true })
    }
  }

  // Resources shown in FullCalendar. A child row (a technician under a
  // manager, a material resource under its category) can only render when its
  // parent row is present, so we always pull in the ancestors of any visible
  // resource — otherwise checking a technician alone would display nothing.
  const visibleResources = () => {
    const passes = res => {
      if (filter.unchecked.includes(res.id)) return false
      const isMaterial = !!(res.resource || res.resourceCategory || res.resourceGroup)
      if (filter.projectsOnly) return !isMaterial && !!res.projectWorker
      if (isMaterial && !filter.showResources) return false
      if (filter.resourcesOnly && filter.showResources) return isMaterial
      return true
    }

    const byId       = new Map(TOOLS.resources.map(res => [res.id, res]))
    const result     = TOOLS.resources.filter(passes)
    const includedIds = new Set(result.map(res => res.id))

    result.slice().forEach(res => {
      let parentId = res.parentId
      while (parentId && !includedIds.has(parentId)) {
        const parent = byId.get(parentId)
        if (!parent) break
        includedIds.add(parent.id)
        result.push(parent)
        parentId = parent.parentId
      }
    })

    return result
  }

  const filterEvents = () => {
    if (!state.events) return([])

    let visibleEvents = [...state.events]
    const filteredResources = TOOLS.resources.filter(res => !filter.unchecked.includes(res.id))
    const resourcesIds = filteredResources.map(resource => resource.id)

    if (filter.projectsOnly) {
      visibleEvents = visibleEvents.filter(event => {
        const className = event.className || ''
        return className.includes('project-assignment-event') || className.includes('holiday-event')
      })
    }

    if (filter.view.name == CALENDAR_VIEWS.agenda) {
      // Events without a resourceId (e.g. public-holiday backgrounds) are
      // global: keep them regardless of the technician filter.
      visibleEvents = visibleEvents.filter(event => !event.resourceId || resourcesIds.includes(event.resourceId.toString()))
    }
    return setBackgroundColor(visibleEvents)
  }

  const setBackgroundColor = events => {
    if (!events) return([])

    const isAbsence   = event => (event.className || '').includes('absence-event')
    const isHoliday   = event => (event.className || '').includes('holiday-event')
    const isAgenda    = filter.view.name == CALENDAR_VIEWS.agenda
    const isAgendaDay = filter.view.name == CALENDAR_VIEWS.agenda_day

    events.forEach(event => {
      if (isHoliday(event)) return // keeps its background shading in every view
      if (!isAgenda && !isAbsence(event)) {
        event.backgroundColor = 'white'
        event.textColor       = '#646464'
      } else if ((!isAgenda && !isAgendaDay) && isAbsence(event)) {
        event.display = 'background'
      } else if (isAgenda && isAbsence(event)) {
        event.backgroundColor = event.user_color
      }
    })
    return events
  }

  const setView = view => {
    if (state.calendar) state.calendar.changeView(CALENDAR_VIEWS[view.id])
    setFilter(prevFilter=>({...prevFilter, view: getCurrentView(CALENDAR_VIEWS[view.id], TOOLS.slotWidthWeek, TOOLS.slotWidthMonth)}))
  }

  const stackedMode = filter.view.name === CALENDAR_VIEWS.agenda && filter.stackedAgenda

  const handleSelectAllow = info => !info.resource || !isMaterialPlanningRow(info.resource._resource.id)

  const handleEventAllow = (dropInfo, draggedEvent) => {
    // Same rule as the old per-event `constraint`, without the payload cost:
    // only technician rows (or the open pool) accept a drop.
    if (TOOLS.droppableResources && dropInfo.resource && !TOOLS.droppableResources.includes(dropInfo.resource.id)) return false

    const earliest = draggedEvent.extendedProps.info?.earliestPlanningDate
    if (!earliest) return true

    const [year, month, day] = earliest.split('-').map(Number)
    return dropInfo.start >= new Date(year, month - 1, day)
  }

  return (
    <div className="calendar-right">
      { (filter.showOpen || filter.showReceivedParts) && TOOLS.displayOpen == SEPARATED &&
         <OpenEventsList />
      }

      <div className="fullcalendar-wrapper">
        <Combobox
          options         = {Object.values(calendarViews)}
          onSelectElement = {setView}
          value           = {Object.keys(CALENDAR_VIEWS).find(key => CALENDAR_VIEWS[key] == filter.view.name)}
          className       = 'select-view'
        />
        { stackedMode ?
          <StackedCalendar
            renderEventContent = {renderEventContent}
            onEventDrop        = {handleInternalDrop}
            onEventResize      = {handleEventResize}
          />
        :
        <FullCalendar
          schedulerLicenseKey   = {TOOLS.license}
          timeZone              = {TOOLS.timeZone}
          height                = {filter.showOpen && TOOLS.displayOpen == SEPARATED ? 'calc(100vh - 300px)' : 'calc(100vh - 130px)'}
          resourceOrder         = 'order,title'
          resourceAreaHeaderContent = {() => ({ html: `<span class="resource-area-header">${I18n.t('calendar.team_column')}</span>` })}
          plugins               = {
            [
              scrollGrid,
              interactionPlugin,
              resourceTimelinePlugin,
              resourceTimeGridPlugin,
              timeGridPlugin,
              daygridplugin,
              resourceDayGridPlugin,
              resource
            ]
          }
          initialView           = {filter.view.name}
          initialDate           = {new URLSearchParams(window.location.search).get('selected_date')}
          headerToolbar         = {{
            left:   `prev,next,today${TOOLS.newAbsences ? ',absence,onCallDuty' : ''}`,
            center: 'title',
            right:  ''
          }}
          views                 = {{ assignment: {type: 'resourceTimelineWeek'} }}
          buttonText            = {{
            today:            I18n.t("workorders.list_of_the_day"),
            resourceTimeline: I18n.t('preferences.calendar.view_day'),
            timeGridWeek:     I18n.t('preferences.calendar.view_agenda'),
            day:              I18n.t('preferences.calendar.view_agenda_day'),
            week:             I18n.t('preferences.calendar.view_week'),
            assignment:       I18n.t('preferences.calendar.view_assignment'),
            month:            I18n.t('preferences.calendar.view_month')
          }}
          weekNumberCalculation ="ISO"
          slotMinTime           = {`${filter.slotMinTime}:00:00`}
          slotMaxTime           = {`${filter.slotMaxTime}:00:00`}
          resourceAreaWidth     ='15%'
          weekends              = {filter.showWeekends}
          events                = {filterEvents()}
          resources             = {visibleResources()}
          datesSet              = {handleDatesSet}
          select                = {handleSelect}
          selectAllow           = {handleSelectAllow}
          eventResize           = {handleEventResize}
          eventContent          = {renderEventContent}
          eventDrop             = {handleInternalDrop}
          eventAllow            = {handleEventAllow}
          drop                  = {handleExternalDrop}
          resourceLabelContent  = {renderLabelContent}
          eventMouseEnter       = {e => setState(prevState=>({...prevState, showSmallCard: e}))}
          eventMouseLeave       = {() => setState(prevState=>({...prevState, showSmallCard: false}))}
          slotMinWidth          = {filter.view.slotMinWidth}
          slotLabelInterval     = {filter.view.slotInterval}
          slotDuration          = {filter.view.slotDuration || '00:30:00'}
          eventTimeFormat       = {{ meridiem: false, hour12: false }}
          locale                = {I18n.locale}
          dayMinWidth           = {150}
          customButtons         = {customButtons}
          weekText              = {I18n.t("date.week_short")}
          weekNumbers
          selectable
          eventResourceEditable
          nowIndicator
          allDaySlot
        />
        }
      </div>
    </div>
  )
}

export default CalendarRight
